import { useEffect, useState } from "react";
import { getLatestVerionIDForBlueprint } from "../../utils/BlueprintEditorAPIClient";

// Shared across every step card, so cards pointing at the same Functional Blueprint fetch once.
const versionIDsByBlueprintID = new Map<string, string>();
const requestsByBlueprintID = new Map<string, Promise<string | null>>();

const fetchLatestVersionIDOnce = (blueprintID: string): Promise<string | null> => {
  const cachedVersionID = versionIDsByBlueprintID.get(blueprintID);
  if (cachedVersionID) {
    return Promise.resolve(cachedVersionID);
  }

  const inFlightRequest = requestsByBlueprintID.get(blueprintID);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = new Promise<string | null>((resolve) => {
    getLatestVerionIDForBlueprint({
      blueprintID,
      onSuccess: (response: any) => {
        const versionID = response?.blueprint_version_id ?? null;
        if (versionID) {
          versionIDsByBlueprintID.set(blueprintID, versionID);
        }
        resolve(versionID);
      },
      // Left uncached so a later render can retry.
      onError: () => resolve(null),
    });
  });

  requestsByBlueprintID.set(blueprintID, request);
  request.then(() => requestsByBlueprintID.delete(blueprintID));

  return request;
};

/** Resolves the latest Blueprint version ID, fetching at most once per Blueprint. */
const useLatestBlueprintVersionID = (
  blueprintID: string | undefined,
  isEnabled: boolean
): string | null => {
  // state
  const [versionID, setVersionID] = useState<string | null>(
    blueprintID ? versionIDsByBlueprintID.get(blueprintID) ?? null : null
  );

  // effects
  // Deliberately keyed on the Blueprint ID rather than the step, so replacing the
  // blueprint object does not refetch.
  useEffect(() => {
    if (!isEnabled || !blueprintID) {
      return;
    }

    let isSubscribed = true;
    fetchLatestVersionIDOnce(blueprintID).then((resolvedVersionID) => {
      if (isSubscribed) {
        setVersionID(resolvedVersionID);
      }
    });

    return () => {
      isSubscribed = false;
    };
  }, [blueprintID, isEnabled]);

  return versionID;
};

export default useLatestBlueprintVersionID;
